<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { computed } from "vue";
import {
  useImage,
  AtomButton,
  AtomText,
  SatButton,
} from "@chatfood/design-system";
import { useLocale } from "~/composables/use-locale";
import { useNavigation } from "~/composables/use-navigation";
import { useBusinessStore } from "~/stores/business";
import type { BrandingMediaName } from "~/stores/branding";
import { useBrandingStore } from "~/stores/branding";
import { useLoyaltyProgramStore } from "~/stores/loyaltyProgram";
import { useRichCard } from "~/composables/use-rich-card";
import { LoyaltyLandingPage } from "~/components/loyalty-landing-page";
import { SocialMediaLinks } from "~/components/social-media-links";
import { PastOrders } from "~/components/past-orders";

const css = bemBuilder("page-home");

const { t } = useLocale();
const { optimize } = useImage();
const { push } = useNavigation();

const businessStore = useBusinessStore();
const brandingStore = useBrandingStore();
const loyaltyProgramStore = useLoyaltyProgramStore();

const coverImage = computed(() => {
  return (
    brandingStore.getMediaUrl("marketing_cover_mobile") ?? businessStore.cover
  );
});

useRichCard({
  title: businessStore.name,
  ...(coverImage.value ? { image: coverImage.value } : {}),
  description: t("pages.landing_page.seo_description", {
    businessName: businessStore.name,
  }),
});

const isNativeLoyaltyEnabled = computed(() => {
  return loyaltyProgramStore.isEnabled && loyaltyProgramStore.isNative;
});

const toHomePage = () => {
  push("order", {});
};

const mediaKeys: BrandingMediaName[] = [
  "lp_image_1",
  "lp_image_2",
  "lp_image_3",
  "lp_image_4",
];

const medias = computed(() =>
  mediaKeys
    .map(brandingStore.getMediaUrl)
    .filter((media) => media !== undefined),
);

const loadNativeLoyalty = async () => {
  if (loyaltyProgramStore.isLoaded) return;

  try {
    await loyaltyProgramStore.load({
      businessId: businessStore.id,
      salesChannel: businessStore.salesChannel,
    });
  } catch {
    // silent error
  }
};

loadNativeLoyalty();
</script>

<template>
  <div :class="css()">
    <div :class="css('wrapper')">
      <div
        v-if="coverImage"
        :class="css('cover')"
        :style="`background-image: url(${optimize(coverImage, {
          w: 640,
        })})`"
      />
      <div :class="css('content')">
        <div :class="css('middle-content')">
          <LoyaltyLandingPage v-if="isNativeLoyaltyEnabled" />
          <PastOrders :class="css('past-orders')">
            <div :class="css('past-orders-header')">
              <AtomText type="subtitle">
                {{ t("pages.home.recent_orders") }}
              </AtomText>
              <SatButton
                size="medium"
                theme="ghost"
                variant="primary"
                :route="{ name: 'my-account.order-history' }"
              >
                {{ t("pages.home.see_all_orders") }}
              </SatButton>
            </div>
          </PastOrders>
          <div v-if="medias.length">
            <AtomText type="h3">
              {{ t("pages.landing_page.medias_title") }}
            </AtomText>
            <div :class="css('medias')">
              <div
                v-for="media in medias"
                :key="media"
                :style="`background-image: url(${optimize(media, {
                  w: 640,
                })})`"
                :class="css('medias-image')"
              ></div>
            </div>
          </div>
        </div>
        <SocialMediaLinks v-if="businessStore.isMobileApp" />
      </div>
    </div>
    <div :class="css('ctas')">
      <AtomButton
        :class="css('order-button')"
        size="large"
        type="button"
        variant="primary"
        @click="toHomePage"
      >
        {{ t("pages.landing_page.order_cta") }}
      </AtomButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.page-home {
  display: flex;
  max-width: 640px;
  min-height: 100dvh;
  flex-direction: column;
  margin: 0 auto;

  @media (width >= $breakpoint-sm) {
    min-height: auto;
    margin-bottom: none;
    padding-block: var(--spacing-2xl);
  }

  &__wrapper {
    flex: 1;
    display: flex;
    gap: var(--spacing-lg);
    flex-direction: column;
  }

  &__cover {
    display: block;
    max-width: 100%;
    border-radius: 0;
    aspect-ratio: 16 / 9;
    background-size: cover;
    background-position: center;

    @media (width >= $breakpoint-sm) {
      border-radius: var(--radii-card-md);
    }
  }

  &__content {
    flex: 1;
    display: flex;
    gap: var(--spacing-md);
    flex-direction: column;
    padding-inline: var(--spacing-lg);

    @media (width >= $breakpoint-sm) {
      padding-inline: 0;
    }
  }

  &__past-orders {
    padding-block: var(--spacing-lg);
    background: var(--color-base-90);

    &-header {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      justify-content: space-between;
      padding-inline: var(--spacing-lg);
      padding-bottom: var(--spacing-sm);
    }
  }

  &__middle-content {
    flex: 1;
    display: flex;
    gap: var(--spacing-xl);
    flex-direction: column;

    @media (width >= $breakpoint-sm) {
      gap: var(--spacing-lg);
    }
  }

  &__medias {
    display: grid;
    gap: var(--spacing-lg);
    padding-block: var(--spacing-sm);

    @media (width >= $breakpoint-sm) {
      grid-template-columns: 1fr 1fr;
      padding-bottom: var(--spacing-lg);
    }

    &-image {
      width: 100%;
      display: block;
      aspect-ratio: 16 / 9;
      background-size: cover;
      background-position: center;
    }
  }

  &__ctas {
    position: sticky;
    bottom: 0;
    padding: var(--spacing-md) var(--spacing-lg);
    background-color: var(--color-base-100);

    @media (width >= $breakpoint-sm) {
      position: unset;
      background-color: transparent;
      text-align: center;
    }
  }

  &__order-button {
    width: 100%;

    @media (width >= $breakpoint-sm) {
      max-width: 300px;
      margin-inline: auto;
    }
  }
}
</style>
